import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Plot from "react-plotly.js";
import { Box, Typography, useTheme } from "@mui/material";
import "../../style/sidemenu-pages-style/EPDPlot.css";

const EPDPlot = ({ gwpValues, calculateStandardDeviation }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [gwpStdDev, setGwpStdDev] = useState(null);

  useEffect(() => {
    if (gwpValues && calculateStandardDeviation) {
      const stdDev = calculateStandardDeviation(gwpValues);
      setGwpStdDev(stdDev);
    }
  }, [gwpValues, calculateStandardDeviation]);

  if (!gwpValues || gwpValues.length === 0) {
    return null;
  }

  const gwpMin = Math.min(...gwpValues);
  const gwpMax = Math.max(...gwpValues);
  const gwpMean = gwpValues.reduce((sum, value) => sum + value, 0) / gwpValues.length || 0;
  const gwpStdDevFormatted = gwpStdDev !== null ? gwpStdDev.toFixed(2) : "N/A";

  const lineColor = isDark ? "#4ade80" : "#16a34a";
  const fillColor = isDark ? "rgba(74,222,128,0.2)" : "rgba(34,197,94,0.12)";
  const markerColor = isDark ? "#6366f1" : "#4f46e5";
  const textColor = isDark ? "#cbd5e1" : "#475569";
  const gridColor = isDark ? "#2d2d34" : "#e2e8f0";

  return (
    <Box
      className="gwp-boxplot"
      sx={{
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        border: `1.5px solid ${isDark ? "#2d2d34" : "#e2e8f0"}`,
        borderRadius: "16px",
        p: 3,
        mt: 3,
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.15)" : "0 4px 12px rgba(0,0,0,0.03)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", height: "420px", display: "flex", justifyContent: "center" }}>
        <Plot
          useResizeHandler
          style={{ width: "100%", height: "100%" }}
          data={[
            {
              type: "box",
              y: gwpValues,
              name: t("GWP Distribution"),
              boxpoints: "all",
              jitter: 0.35,
              pointpos: 0,
              fillcolor: fillColor,
              marker: {
                color: markerColor,
                size: 5,
                opacity: 0.55,
              },
              line: {
                color: lineColor,
                width: 2,
              },
            },
          ]}
          layout={{
            autosize: true,
            title: {
              text: t("GWP Distribution"),
              font: {
                family: "Poppins, sans-serif",
                size: 18,
                weight: 700,
                color: isDark ? "#ffffff" : "#1e293b",
              },
              yref: "container",
              y: 0.95,
            },
            yaxis: {
              title: {
                text: `${t("GWP")} (kg CO₂e)`,
                font: {
                  family: "Poppins, sans-serif",
                  size: 13,
                  color: textColor,
                },
              },
              gridcolor: gridColor,
              zerolinecolor: gridColor,
              tickfont: {
                family: "Poppins, sans-serif",
                size: 11,
                color: textColor,
              },
            },
            xaxis: {
              showticklabels: false,
              gridcolor: gridColor,
              zerolinecolor: gridColor,
            },
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            showlegend: false,
            margin: { t: 60, b: 30, l: 60, r: 40 },
          }}
          config={{
            responsive: true,
            displayModeBar: false,
          }}
        />
      </Box>

      {/* Styled Statistics Dashboard Card */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: 2, sm: 4 },
          flexWrap: "wrap",
          width: "100%",
          maxWidth: "750px",
          mt: 2,
          p: 2,
          borderRadius: "12px",
          backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
          border: `1.5px solid ${isDark ? "#2d2d34" : "#e2e8f0"}`,
        }}
      >
        <Box sx={{ textAlign: "center", px: 1.5 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("Min GWP")}
          </Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: isDark ? "#4ade80" : "#16a34a", mt: 0.5, fontFamily: "Poppins, sans-serif" }}>
            {gwpMin.toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{ width: "1px", backgroundColor: isDark ? "#2d2d34" : "#e2e8f0", alignSelf: "stretch" }} />

        <Box sx={{ textAlign: "center", px: 1.5 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("Max GWP")}
          </Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: isDark ? "#ef4444" : "#dc2626", mt: 0.5, fontFamily: "Poppins, sans-serif" }}>
            {gwpMax.toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{ width: "1px", backgroundColor: isDark ? "#2d2d34" : "#e2e8f0", alignSelf: "stretch" }} />

        <Box sx={{ textAlign: "center", px: 1.5 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("Mean")}
          </Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: isDark ? "#3b82f6" : "#2563eb", mt: 0.5, fontFamily: "Poppins, sans-serif" }}>
            {gwpMean.toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{ width: "1px", backgroundColor: isDark ? "#2d2d34" : "#e2e8f0", alignSelf: "stretch" }} />

        <Box sx={{ textAlign: "center", px: 1.5 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("Std Dev")}
          </Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: isDark ? "#a855f7" : "#7c3aed", mt: 0.5, fontFamily: "Poppins, sans-serif" }}>
            {gwpStdDevFormatted}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default EPDPlot;
